import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { ChildExtrasService } from "@/api";
import type {
  child_extras_ChildExtraResponse,
  child_extras_ChildExtrasListResponse,
  child_extras_CreateChildExtraRequest,
  child_extras_UpdateChildExtraRequest,
} from "@/api";
import { invalidateMoneyViews } from "./invalidateMoneyViews";

// childExtrasKeys centralises the TanStack Query namespace for the ad-hoc
// "extras" feature (issue #565). The list is keyed by the debtor account it is
// displayed under (debtor → contract_payers → contracts → child).
export const childExtrasKeys = {
  all: ["reconciliation", "child-extras"] as const,
  listByDebtor: (debtorId: string, limit: number, offset: number) =>
    [...childExtrasKeys.all, "by-debtor", debtorId, { limit, offset }] as const,
};

export function useDebtorExtras(
  debtorId: string | null,
  limit = 50,
  offset = 0,
) {
  return useQuery<child_extras_ChildExtrasListResponse>({
    queryKey: debtorId
      ? childExtrasKeys.listByDebtor(debtorId, limit, offset)
      : [...childExtrasKeys.all, "none"],
    queryFn: () =>
      ChildExtrasService.getApiV1DebtorAccountsExtras(
        debtorId as string,
        limit,
        offset,
      ),
    enabled: !!debtorId,
    placeholderData: keepPreviousData,
  });
}

async function invalidateExtras(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  // An extra is a billing INPUT: it does not move a debtor metric until it is
  // billed, but the invoice preview, the next generation run and the debtor
  // slide-over all read it. Routing through the shared list keeps that honest
  // and, more to the point, broadcasts to the operator's other browser tabs —
  // adding an extra in one tab used to leave the invoice open in another tab
  // showing a total that no longer matched.
  await invalidateMoneyViews(queryClient);
}

export interface CreateChildExtraInput {
  childId: string;
  body: child_extras_CreateChildExtraRequest;
}

export function useCreateChildExtra() {
  const queryClient = useQueryClient();
  return useMutation<child_extras_ChildExtraResponse, unknown, CreateChildExtraInput>({
    mutationFn: ({ childId, body }) =>
      ChildExtrasService.postApiV1ChildrenExtras(childId, body),
    onSuccess: async () => invalidateExtras(queryClient),
  });
}

export interface UpdateChildExtraInput {
  id: string;
  body: child_extras_UpdateChildExtraRequest;
}

export function useUpdateChildExtra() {
  const queryClient = useQueryClient();
  return useMutation<child_extras_ChildExtraResponse, unknown, UpdateChildExtraInput>({
    mutationFn: ({ id, body }) => ChildExtrasService.patchApiV1Extras(id, body),
    onSuccess: async () => invalidateExtras(queryClient),
  });
}

export function useDeleteChildExtra() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, { id: string }>({
    mutationFn: ({ id }) =>
      ChildExtrasService.deleteApiV1Extras(id) as unknown as Promise<void>,
    onSuccess: async () => invalidateExtras(queryClient),
  });
}
